<template>
  <div class="zoom-control" v-click-outside-zoom="closePanel">
    <span
      class="zoom-toggle"
      :class="{ active: isOpen || zoom !== defaultZoom }"
      :title="$t('uiZoom.title')"
      @click.stop="togglePanel"
    >
      <i class="material-icons">zoom_in</i>
    </span>

    <div v-if="isOpen" ref="panel" class="zoom-panel" @click.stop>
      <button
        type="button"
        class="zoom-btn"
        :title="$t('uiZoom.zoomOut')"
        :disabled="zoom <= minZoom"
        @click="changeZoom(-step)"
      >
        <i class="material-icons">remove</i>
      </button>
      <button
        type="button"
        class="zoom-value"
        :title="$t('uiZoom.reset')"
        @click="setZoom(defaultZoom)"
      >
        {{ zoom }}%
      </button>
      <button
        type="button"
        class="zoom-btn"
        :title="$t('uiZoom.zoomIn')"
        :disabled="zoom >= maxZoom"
        @click="changeZoom(step)"
      >
        <i class="material-icons">add</i>
      </button>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  UI_ZOOM_MIN,
  UI_ZOOM_MAX,
  UI_ZOOM_STEP,
  UI_ZOOM_DEFAULT,
  normalizeUiZoom,
  applyUiZoom,
  readCachedUiZoom,
  cacheUiZoom,
} from "@/utils/uiZoom";

const SAVE_DEBOUNCE_MS = 600;

export default {
  name: "ZoomControl",
  directives: {
    clickOutsideZoom: {
      bind(el, binding) {
        el.__zoomClickOutside__ = (event) => {
          if (!el.contains(event.target)) binding.value(event);
        };
        document.addEventListener("click", el.__zoomClickOutside__);
      },
      unbind(el) {
        document.removeEventListener("click", el.__zoomClickOutside__);
        delete el.__zoomClickOutside__;
      },
    },
  },
  data() {
    return {
      zoom: UI_ZOOM_DEFAULT,
      isOpen: false,
      saveTimer: null,
      minZoom: UI_ZOOM_MIN,
      maxZoom: UI_ZOOM_MAX,
      step: UI_ZOOM_STEP,
      defaultZoom: UI_ZOOM_DEFAULT,
    };
  },
  mounted() {
    this.loadZoomPreference();
    this.watchSidebarCollapse();
  },
  beforeDestroy() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.persistZoom();
    }
    if (this.sidebarObserver) this.sidebarObserver.disconnect();
    if (this.sidebarEl) {
      this.sidebarEl.removeEventListener("mouseleave", this.onSidebarLeave);
    }
  },
  methods: {
    togglePanel() {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) return;
      // El pie del sidebar está al fondo del área con scroll: que el panel quede a la vista.
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ block: "nearest" });
        }
      });
    },
    /** Al contraer el sidebar (toggle o fin del hover en modo mini) el panel no entra: se cierra. */
    watchSidebarCollapse() {
      this.sidebarEl = this.$el.closest(".sidebar");
      if (!this.sidebarEl) return;

      const layoutState = () =>
        `${document.body.classList.contains(
          "sidebar-mini"
        )}|${this.sidebarEl.classList.contains("sidebar-expanded")}`;
      let lastState = layoutState();
      this.sidebarObserver = new MutationObserver(() => {
        const state = layoutState();
        if (state !== lastState) {
          lastState = state;
          this.closePanel();
        }
      });
      this.sidebarObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
      this.sidebarObserver.observe(this.sidebarEl, {
        attributes: true,
        attributeFilter: ["class"],
      });

      this.onSidebarLeave = () => {
        if (document.body.classList.contains("sidebar-mini")) this.closePanel();
      };
      this.sidebarEl.addEventListener("mouseleave", this.onSidebarLeave);
    },
    async loadZoomPreference() {
      const userId = localStorage.getItem("userId");
      this.zoom = applyUiZoom(readCachedUiZoom(userId));

      if (!localStorage.getItem("token") || !userId) return;

      try {
        const response = await wencesApi.get("/api/users/myprofile");
        if (response?.data?.ok && response?.data?.data) {
          // No pisar un cambio que el usuario hizo mientras cargaba el perfil.
          if (this.saveTimer) return;
          this.zoom = applyUiZoom(response.data.data.uiZoom);
          cacheUiZoom(userId, this.zoom);
        }
      } catch (error) {
        console.error("Error loading zoom preference:", error);
      }
    },
    changeZoom(delta) {
      this.setZoom(this.zoom + delta);
    },
    setZoom(value) {
      const next = normalizeUiZoom(value);
      if (next === this.zoom) return;
      this.zoom = applyUiZoom(next);
      cacheUiZoom(localStorage.getItem("userId"), this.zoom);

      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(this.persistZoom, SAVE_DEBOUNCE_MS);
    },
    async persistZoom() {
      this.saveTimer = null;
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      try {
        await wencesApi.post("/api/users/edit", {
          user: {
            _id: userId,
            uiZoom: this.zoom,
          },
        });
      } catch (error) {
        // El zoom queda aplicado y en cache local; se reintenta en el próximo cambio.
        console.error("Error saving zoom preference:", error);
      }
    },
    closePanel() {
      this.isOpen = false;
    },
  },
};
</script>

<style scoped>
.zoom-control {
  position: relative;
  display: flex;
  align-items: center;
}

/* Tamaño fijo: círculo del mismo alto que el switch de tema (30px expandido, 38px en mini). */
.zoom-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.sidebar-mini .sidebar:not(.sidebar-expanded) .zoom-toggle {
  width: 38px;
  height: 38px;
}

.zoom-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* `.sidebar .nav i` (demo.css) le pone ancho, alto, float y margin-right/font-size con
   !important y descentraba la lupa: hace falta más especificidad que esa regla. */
.zoom-control .zoom-toggle i {
  float: none !important;
  width: auto !important;
  height: auto !important;
  margin: 0 !important;
  line-height: 1 !important;
  font-size: 18px !important;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.3s ease;
}

.sidebar-mini .sidebar:not(.sidebar-expanded) .zoom-control .zoom-toggle i {
  font-size: 22px !important;
}

.zoom-toggle.active i {
  color: #7c9ed9;
}

/* Se despliega hacia abajo y alineado al borde derecho de la lupa, hacia adentro del sidebar. */
.zoom-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 20px;
  background: #2b3643;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  white-space: nowrap;
}

.zoom-btn,
.zoom-value {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  border-radius: 16px;
  transition: background 0.2s ease;
}

.zoom-btn {
  width: 28px;
  height: 28px;
  padding: 0;
}

.zoom-control .zoom-btn i {
  float: none !important;
  width: auto !important;
  height: auto !important;
  margin: 0 !important;
  line-height: 1 !important;
  font-size: 18px !important;
}

.zoom-value {
  min-width: 48px;
  height: 28px;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 500;
}

.zoom-btn:hover:not(:disabled),
.zoom-value:hover {
  background: rgba(255, 255, 255, 0.12);
}

.zoom-btn:disabled {
  opacity: 0.35;
  cursor: default;
}
</style>
